/**
 * PF MolView - Main Application Entry Point
 *
 * A feature-rich web-based molecular viewer inspired by VeraChem's PF MolView
 */

import { MoleculeViewer } from './lib/viewer';
import type { RenderStyle, LabelType, ColorScheme, SymmetryDisplayMode, MoleculeEntry } from './lib/types';
import { MoleculeStore } from './lib/molecule-store';
import { loadFile, getSupportedExtensions, parseFromString } from './lib/file-loader';
import { logger, LogCategories } from './lib/logger';
import './style.css';

// Declare 3Dmol global
declare const $3Dmol: any;

// Wait for DOM and 3Dmol to be ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if 3Dmol is loaded
  if (typeof $3Dmol === 'undefined') {
    console.error('3Dmol.js not loaded!');
    document.getElementById('viewer-container')!.innerHTML =
      '<div class="error">Error: 3Dmol.js failed to load. Please check your internet connection.</div>';
    return;
  }

  initApp();
});

function initApp(): void {
  // Initialize core components
  const viewer = new MoleculeViewer('viewer-container', {
    backgroundColor: '#ffffff', // white
  });

  const store = new MoleculeStore();

  // Get DOM elements
  const fileInput = document.getElementById('file-input') as HTMLInputElement;
  const moleculeSelect = document.getElementById('molecule-select') as HTMLSelectElement;
  const infoPanel = document.getElementById('info-panel') as HTMLDivElement;
  const prevBtn = document.getElementById('prev-btn') as HTMLButtonElement;
  const nextBtn = document.getElementById('next-btn') as HTMLButtonElement;
  const removeBtn = document.getElementById('remove-btn') as HTMLButtonElement;
  const clearBtn = document.getElementById('clear-btn') as HTMLButtonElement;
  const styleSelect = document.getElementById('style-select') as HTMLSelectElement;
  const statusText = document.getElementById('status-text') as HTMLSpanElement;
  const moleculeCount = document.getElementById('molecule-count') as HTMLSpanElement;
  const moleculeIndex = document.getElementById('molecule-index') as HTMLSpanElement;
  const dropZone = document.getElementById('drop-zone') as HTMLDivElement;

  // Control panel elements
  const bgSelect = document.getElementById('bg-select') as HTMLSelectElement;
  const fogToggle = document.getElementById('fog-toggle') as HTMLInputElement;
  const spinToggle = document.getElementById('spin-toggle') as HTMLInputElement;
  const centerBtn = document.getElementById('center-btn') as HTMLButtonElement;
  const renderStyleSelect = document.getElementById('render-style-select') as HTMLSelectElement;
  const colorSchemeSelect = document.getElementById('color-scheme-select') as HTMLSelectElement;
  const hydrogensToggle = document.getElementById('hydrogens-toggle') as HTMLInputElement;
  const labelTypeSelect = document.getElementById('label-type-select') as HTMLSelectElement;
  const labelAllBtn = document.getElementById('label-all-btn') as HTMLButtonElement;
  const clearLabelsBtn = document.getElementById('clear-labels-btn') as HTMLButtonElement;
  const quickSelectToggle = document.getElementById('quick-select-toggle') as HTMLInputElement;

  // Measurements panel elements
  const measurementList = document.getElementById('measurement-list') as HTMLDivElement;
  const selectionProgressRow = document.getElementById('selection-progress-row') as HTMLDivElement;
  const selectionProgress = document.getElementById('selection-progress') as HTMLSpanElement;
  const copyMeasurementsBtn = document.getElementById('copy-measurements-btn') as HTMLButtonElement;
  const exportCsvBtn = document.getElementById('export-csv-btn') as HTMLButtonElement;
  const clearMeasurementsBtn = document.getElementById('clear-measurements-btn') as HTMLButtonElement;

  // Color Symmetry panel elements
  const symmetryEnabledCheckbox = document.getElementById('symmetry-enabled-checkbox') as HTMLInputElement;
  const symmetryModeRow = document.getElementById('symmetry-mode-row') as HTMLDivElement;
  const symmetryModeSelect = document.getElementById('symmetry-mode-select') as HTMLSelectElement;
  const symmetrySelectorRow = document.getElementById('symmetry-selector-row') as HTMLDivElement;
  const symmetrySelectorLabel = document.getElementById('symmetry-selector-label') as HTMLLabelElement;
  const symmetryGroupSelect = document.getElementById('symmetry-group-select') as HTMLSelectElement;
  const symmetryNavRow = document.getElementById('symmetry-nav-row') as HTMLDivElement;
  const symmetryPrevBtn = document.getElementById('symmetry-prev-btn') as HTMLButtonElement;
  const symmetryNextBtn = document.getElementById('symmetry-next-btn') as HTMLButtonElement;
  const symmetryStatus = document.getElementById('symmetry-status') as HTMLParagraphElement;

  // Log UI initialization
  logger.info(LogCategories.UI, 'UI elements initialized');

  // Set accepted file types
  fileInput.accept = getSupportedExtensions();

  // =========================================================================
  // File Loading
  // =========================================================================

  async function handleFiles(files: FileList | File[]): Promise<void> {
    const fileArray = Array.from(files);
    let totalLoaded = 0;
    const errors: string[] = [];

    setStatus(`Loading ${fileArray.length} file(s)...`);

    for (const file of fileArray) {
      try {
        const molecules = await loadFile(file);
        store.addAll(molecules);
        totalLoaded += molecules.length;
      } catch (err) {
        errors.push(`${file.name}: ${(err as Error).message}`);
      }
    }

    if (errors.length > 0) {
      console.error('Load errors:', errors);
      setStatus(`Loaded ${totalLoaded} molecule(s), ${errors.length} error(s)`);
    } else {
      setStatus(`Loaded ${totalLoaded} molecule(s)`);
    }
  }

  // File input change handler
  fileInput.addEventListener('change', async () => {
    if (fileInput.files && fileInput.files.length > 0) {
      await handleFiles(fileInput.files);
      fileInput.value = ''; // Reset input
    }
  });

  // Drag and drop handlers
  dropZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropZone.classList.add('drag-over');
  });

  dropZone.addEventListener('dragleave', (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropZone.classList.remove('drag-over');
  });

  dropZone.addEventListener('drop', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropZone.classList.remove('drag-over');

    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files);
    }
  });

  // =========================================================================
  // Molecule Selection
  // =========================================================================

  moleculeSelect.addEventListener('change', () => {
    const selectedIndex = moleculeSelect.selectedIndex;
    if (selectedIndex >= 0) {
      store.select(selectedIndex);
    }
  });

  // =========================================================================
  // Navigation
  // =========================================================================

  prevBtn.addEventListener('click', () => store.prev());
  nextBtn.addEventListener('click', () => store.next());

  // Keyboard navigation
  document.addEventListener('keydown', (e) => {
    // Ignore if user is typing in an input
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }

    switch (e.key) {
      case 'ArrowLeft':
        store.prev();
        e.preventDefault();
        break;
      case 'ArrowRight':
        store.next();
        e.preventDefault();
        break;
      case 'Delete':
      case 'Backspace':
        store.removeCurrent();
        e.preventDefault();
        break;
      case 'Home':
        store.first();
        e.preventDefault();
        break;
      case 'End':
        store.last();
        e.preventDefault();
        break;
    }
  });

  // =========================================================================
  // Remove/Clear
  // =========================================================================

  removeBtn.addEventListener('click', () => {
    store.removeCurrent();
  });

  clearBtn.addEventListener('click', () => {
    if (store.count() === 0) return;
    if (confirm(`Remove all ${store.count()} molecule(s)?`)) {
      store.clear();
      viewer.clear();
      setStatus('All molecules cleared');
    }
  });

  // =========================================================================
  // Style Selection (Quick Access)
  // =========================================================================

  styleSelect.addEventListener('change', () => {
    const style = styleSelect.value as RenderStyle;
    viewer.applyStyle(style);
    // Sync with control panel
    renderStyleSelect.value = style;
    setStatus(`Style: ${style}`);
  });

  // =========================================================================
  // Control Panel - View
  // =========================================================================

  bgSelect.addEventListener('change', () => {
    const color = bgSelect.value;
    viewer.setBackgroundColor(color);
    setStatus(`Background: ${color}`);
  });

  fogToggle.addEventListener('change', () => {
    viewer.setFog(fogToggle.checked);
    setStatus(fogToggle.checked ? 'Fog enabled' : 'Fog disabled');
  });

  spinToggle.addEventListener('change', () => {
    viewer.spin(spinToggle.checked);
    setStatus(spinToggle.checked ? 'Auto-spin enabled' : 'Auto-spin disabled');
  });

  centerBtn.addEventListener('click', () => {
    viewer.centerView();
    setStatus('View centered');
  });

  // =========================================================================
  // Control Panel - Graphics
  // =========================================================================

  renderStyleSelect.addEventListener('change', () => {
    const style = renderStyleSelect.value as RenderStyle;
    viewer.applyStyle(style);
    // Sync with quick access
    styleSelect.value = style;
    setStatus(`Style: ${style}`);
  });

  colorSchemeSelect.addEventListener('change', () => {
    const scheme = colorSchemeSelect.value as ColorScheme;
    viewer.setColorScheme(scheme);
    setStatus(`Color scheme: ${scheme}`);
  });

  hydrogensToggle.addEventListener('change', () => {
    logger.debug(LogCategories.UI, 'Hydrogens toggle changed', { checked: hydrogensToggle.checked });
    viewer.setHydrogensVisible(hydrogensToggle.checked);
    setStatus(hydrogensToggle.checked ? 'Hydrogens visible' : 'Hydrogens hidden');
  });

  // =========================================================================
  // Control Panel - Mouse/Selection
  // =========================================================================

  quickSelectToggle.addEventListener('change', () => {
    logger.debug(LogCategories.UI, 'Quick select toggle changed', { checked: quickSelectToggle.checked });
    viewer.setQuickSelectEnabled(quickSelectToggle.checked);
    selectionProgressRow.style.display = quickSelectToggle.checked ? 'flex' : 'none';
    if (quickSelectToggle.checked) {
      updateSelectionProgress(0);
      setStatus('Quick Select ON - Click atoms to measure');
    } else {
      setStatus('Quick Select OFF');
    }
  });

  // =========================================================================
  // Control Panel - Measurements
  // =========================================================================

  // Set up selection progress callback
  viewer.onSelectionProgress((count: number) => {
    updateSelectionProgress(count);
  });

  // Set up atom click callback to update measurements list
  viewer.onAtomClick(() => {
    // Small delay to allow measurement to be added
    setTimeout(updateMeasurementsList, 50);
  });

  copyMeasurementsBtn.addEventListener('click', async () => {
    const text = formatMeasurementsForClipboard();
    try {
      await navigator.clipboard.writeText(text);
      setStatus('Measurements copied to clipboard');
    } catch (err) {
      console.error('Failed to copy:', err);
      setStatus('Failed to copy to clipboard');
    }
  });

  exportCsvBtn.addEventListener('click', () => {
    const csv = formatMeasurementsAsCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'measurements.csv';
    link.click();
    URL.revokeObjectURL(url);
    setStatus('Measurements exported to CSV');
  });

  clearMeasurementsBtn.addEventListener('click', () => {
    const count = viewer.getMeasurements().length;
    if (count === 0) return;
    if (confirm(`Clear all ${count} measurement(s)?`)) {
      viewer.clearMeasurements();
      updateMeasurementsList();
      setStatus('All measurements cleared');
    }
  });

  // =========================================================================
  // Control Panel - Labels
  // =========================================================================

  labelAllBtn.addEventListener('click', () => {
    const labelType = labelTypeSelect.value as LabelType;
    viewer.addAtomLabels(labelType);
    setStatus(`Labels added: ${labelType}`);
  });

  clearLabelsBtn.addEventListener('click', () => {
    viewer.clearLabels();
    setStatus('Labels cleared');
  });

  // =========================================================================
  // Collapsible Panels
  // =========================================================================

  document.querySelectorAll('.panel-header').forEach((header) => {
    header.addEventListener('click', () => {
      const panel = header.closest('.control-panel');
      if (panel) {
        panel.classList.toggle('expanded');
      }
    });
  });

  // =========================================================================
  // Store Callbacks
  // =========================================================================

  // When selection changes, update viewer and UI
  store.onChange((molecule: MoleculeEntry | null, index: number) => {
    if (molecule) {
      viewer.loadMolecule(molecule.data, molecule.format);
      updateInfoPanel(molecule);
      moleculeSelect.selectedIndex = index;
      setStatus(`Viewing: ${molecule.name}`);

      // Analyze symmetry for the new molecule
      viewer.analyzeSymmetry(false);
      updateSymmetryPanel();
    } else {
      viewer.clear();
      updateInfoPanel(null);
      setStatus('No molecule selected');
      updateSymmetryPanel();
    }
    updateNavigationButtons();
    updateMoleculeIndex();
  });

  // When list changes, update molecule dropdown
  store.onListChange((molecules: MoleculeEntry[]) => {
    updateMoleculeList(molecules);
    updateMoleculeCount(molecules.length);
    updateNavigationButtons();
    updateMoleculeIndex();
  });

  // =========================================================================
  // UI Update Functions
  // =========================================================================

  function updateMoleculeList(molecules: MoleculeEntry[]): void {
    const currentIndex = store.getCurrentIndex();
    moleculeSelect.innerHTML = molecules
      .map((mol, i) => `<option value="${i}" ${i === currentIndex ? 'selected' : ''}>${escapeHtml(mol.name)}</option>`)
      .join('');

    if (molecules.length === 0) {
      moleculeSelect.innerHTML = '<option disabled>No molecules loaded</option>';
    }
  }

  function updateInfoPanel(molecule: MoleculeEntry | null): void {
    if (!molecule) {
      infoPanel.innerHTML = '<p class="empty-message">Load a molecule to view its properties</p>';
      return;
    }

    let html = `
      <div class="info-row">
        <span class="info-label">Name:</span>
        <span class="info-value">${escapeHtml(molecule.name)}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Format:</span>
        <span class="info-value">${molecule.format.toUpperCase()}</span>
      </div>
    `;

    if (molecule.atomCount !== undefined) {
      html += `
        <div class="info-row">
          <span class="info-label">Atoms:</span>
          <span class="info-value">${molecule.atomCount}</span>
        </div>
      `;
    }

    if (molecule.bondCount !== undefined) {
      html += `
        <div class="info-row">
          <span class="info-label">Bonds:</span>
          <span class="info-value">${molecule.bondCount}</span>
        </div>
      `;
    }

    // Display additional properties
    const propEntries = Object.entries(molecule.properties);
    if (propEntries.length > 0) {
      html += '<div class="info-divider"></div>';
      for (const [key, value] of propEntries) {
        html += `
          <div class="info-row">
            <span class="info-label">${escapeHtml(key)}:</span>
            <span class="info-value">${escapeHtml(value)}</span>
          </div>
        `;
      }
    }

    infoPanel.innerHTML = html;
  }

  function updateNavigationButtons(): void {
    const count = store.count();
    prevBtn.disabled = count <= 1;
    nextBtn.disabled = count <= 1;
    removeBtn.disabled = count === 0;
    clearBtn.disabled = count === 0;
  }

  function updateMoleculeCount(count: number): void {
    moleculeCount.textContent = count.toString();
  }

  function updateMoleculeIndex(): void {
    const count = store.count();
    const index = store.getCurrentIndex();
    if (count > 0 && index >= 0) {
      moleculeIndex.textContent = ` (${index + 1} of ${count})`;
    } else {
      moleculeIndex.textContent = '';
    }
  }

  function setStatus(message: string): void {
    statusText.textContent = message;
  }

  function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // =========================================================================
  // Measurement Functions
  // =========================================================================

  function updateMeasurementsList(): void {
    const measurements = viewer.getMeasurements();

    if (measurements.length === 0) {
      measurementList.innerHTML = '<p class="empty-message">No measurements yet</p>';
      copyMeasurementsBtn.disabled = true;
      exportCsvBtn.disabled = true;
      clearMeasurementsBtn.disabled = true;
      return;
    }

    copyMeasurementsBtn.disabled = false;
    exportCsvBtn.disabled = false;
    clearMeasurementsBtn.disabled = false;

    let html = '';
    measurements.forEach((m, index) => {
      let atoms = '';
      if (m.type === 'distance') {
        atoms = `${m.atom1}-${m.atom2}`;
      } else if (m.type === 'angle') {
        atoms = `${m.atom1}-${m.atom2}-${m.atom3}`;
      } else if (m.type === 'dihedral') {
        atoms = `${m.atom1}-${m.atom2}-${m.atom3}-${m.atom4}`;
      }

      html += `
        <div class="measurement-item" data-index="${index}">
          <div class="measurement-info">
            <span class="measurement-type ${m.type}">${m.type}</span>
            <span class="measurement-value">${m.label}</span>
            <span class="measurement-atoms">Atoms: ${atoms}</span>
          </div>
          <button class="measurement-delete" data-index="${index}" title="Delete measurement">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      `;
    });

    measurementList.innerHTML = html;

    // Attach delete handlers
    measurementList.querySelectorAll('.measurement-delete').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const index = parseInt((btn as HTMLElement).dataset.index || '0', 10);
        viewer.deleteMeasurement(index);
        updateMeasurementsList();
        setStatus('Measurement deleted');
      });
    });
  }

  function updateSelectionProgress(count: number): void {
    selectionProgress.textContent = `${count}/4 atoms`;
  }

  function formatMeasurementsForClipboard(): string {
    const measurements = viewer.getMeasurements();
    return measurements.map(m => {
      if (m.type === 'distance') {
        return `Distance: ${m.label} (atoms ${m.atom1}-${m.atom2})`;
      } else if (m.type === 'angle') {
        return `Angle: ${m.label} (atoms ${m.atom1}-${m.atom2}-${m.atom3})`;
      } else {
        return `Dihedral: ${m.label} (atoms ${m.atom1}-${m.atom2}-${m.atom3}-${m.atom4})`;
      }
    }).join('\n');
  }

  function formatMeasurementsAsCsv(): string {
    const measurements = viewer.getMeasurements();
    const header = 'Type,Value,Unit,Atom1,Atom2,Atom3,Atom4\n';
    const rows = measurements.map(m => {
      const unit = m.type === 'distance' ? 'Angstrom' : 'degrees';
      const value = m.value.toFixed(m.type === 'distance' ? 2 : 1);
      if (m.type === 'distance') {
        return `distance,${value},${unit},${m.atom1},${m.atom2},,`;
      } else if (m.type === 'angle') {
        return `angle,${value},${unit},${m.atom1},${m.atom2},${m.atom3},`;
      } else {
        return `dihedral,${value},${unit},${m.atom1},${m.atom2},${m.atom3},${m.atom4}`;
      }
    }).join('\n');
    return header + rows;
  }

  // =========================================================================
  // Color Symmetry Panel
  // =========================================================================

  // Enable/disable symmetry coloring
  symmetryEnabledCheckbox.addEventListener('change', () => {
    const enabled = symmetryEnabledCheckbox.checked;

    if (enabled) {
      viewer.setColorScheme('symmetry');
      symmetryModeRow.style.display = 'flex';
      updateSymmetryPanelVisibility();
    } else {
      viewer.setColorScheme('element');
      symmetryModeRow.style.display = 'none';
      symmetrySelectorRow.style.display = 'none';
      symmetryNavRow.style.display = 'none';
    }
  });

  // Display mode change
  symmetryModeSelect.addEventListener('change', () => {
    const mode = symmetryModeSelect.value as SymmetryDisplayMode;
    viewer.setSymmetryDisplayMode(mode);
    updateSymmetryPanelVisibility();
    updateSymmetrySelector();
  });

  // Group/Ring selector change
  symmetryGroupSelect.addEventListener('change', () => {
    const index = parseInt(symmetryGroupSelect.value, 10);
    const mode = viewer.getSymmetryDisplayMode();

    if (mode === 'ring') {
      viewer.selectRing(index);
    } else if (mode === 'symmetry') {
      viewer.selectSymmetryGroup(index);
    }
  });

  // Navigation buttons
  symmetryPrevBtn.addEventListener('click', () => {
    const mode = viewer.getSymmetryDisplayMode();
    if (mode === 'ring') {
      viewer.prevRing();
      symmetryGroupSelect.value = viewer.getSelectedRingIndex().toString();
    }
  });

  symmetryNextBtn.addEventListener('click', () => {
    const mode = viewer.getSymmetryDisplayMode();
    if (mode === 'ring') {
      viewer.nextRing();
      symmetryGroupSelect.value = viewer.getSelectedRingIndex().toString();
    }
  });

  function updateSymmetryPanel(): void {
    const symmetryResult = viewer.getSymmetryResult();
    const ringInfo = viewer.getRingInfo();

    if (!symmetryResult && !ringInfo) {
      symmetryStatus.textContent = 'Load a molecule to analyze';
      return;
    }

    // Update status message
    const groups = symmetryResult?.groups.length ?? 0;
    const rings = ringInfo?.rings.length ?? 0;
    symmetryStatus.textContent = `${groups} symmetry group(s), ${rings} ring(s) found`;

    // Update selector if enabled
    if (symmetryEnabledCheckbox.checked) {
      updateSymmetrySelector();
    }
  }

  function updateSymmetryPanelVisibility(): void {
    const mode = viewer.getSymmetryDisplayMode();

    // Show selector and nav buttons only for symmetry and ring modes
    if (mode === 'symmetry' || mode === 'ring') {
      symmetrySelectorRow.style.display = 'flex';
      symmetryNavRow.style.display = mode === 'ring' ? 'flex' : 'none';

      // Update label
      symmetrySelectorLabel.textContent = mode === 'ring' ? 'Ring' : 'Group';
    } else {
      symmetrySelectorRow.style.display = 'none';
      symmetryNavRow.style.display = 'none';
    }
  }

  function updateSymmetrySelector(): void {
    const mode = viewer.getSymmetryDisplayMode();
    symmetryGroupSelect.innerHTML = '';

    if (mode === 'symmetry') {
      const result = viewer.getSymmetryResult();
      if (!result || result.groups.length === 0) {
        symmetryGroupSelect.innerHTML = '<option value="-1">No symmetry groups</option>';
        return;
      }

      // Add "All groups" option
      const allOption = document.createElement('option');
      allOption.value = '-1';
      allOption.textContent = 'All groups';
      symmetryGroupSelect.appendChild(allOption);

      // Add each group
      result.groups.forEach((group, i) => {
        const option = document.createElement('option');
        option.value = i.toString();
        option.textContent = `${group.label} (${group.atomIndices.length} atoms)`;
        symmetryGroupSelect.appendChild(option);
      });
    } else if (mode === 'ring') {
      const ringInfo = viewer.getRingInfo();
      if (!ringInfo || ringInfo.rings.length === 0) {
        symmetryGroupSelect.innerHTML = '<option value="-1">No rings found</option>';
        return;
      }

      // Add "All rings" option
      const allOption = document.createElement('option');
      allOption.value = '-1';
      allOption.textContent = 'All rings';
      symmetryGroupSelect.appendChild(allOption);

      // Add each ring
      ringInfo.rings.forEach((ring, i) => {
        const option = document.createElement('option');
        option.value = i.toString();
        option.textContent = `Ring ${i + 1} (${ring.length} atoms)`;
        symmetryGroupSelect.appendChild(option);
      });
    }
  }

  // =========================================================================
  // Initial State
  // =========================================================================

  updateNavigationButtons();
  updateMoleculeCount(0);
  setStatus('Ready - Load a molecule file to begin');

  // =========================================================================
  // Load Default Molecule
  // =========================================================================

  async function loadDefaultMolecule(): Promise<void> {
    try {
      const response = await fetch('./samples/caffeine.sdf');
      if (!response.ok) {
        console.log('Default molecule not found, skipping');
        return;
      }
      const data = await response.text();
      const molecules = parseFromString(data, 'sdf', 'caffeine.sdf');
      if (molecules.length > 0) {
        store.addAll(molecules);
        setStatus('Caffeine loaded - drag & drop to load your own molecules');
      }
    } catch (err) {
      console.log('Could not load default molecule:', err);
    }
  }

  loadDefaultMolecule();

  console.log('PF MolView initialized');
}
